import type { RhythmReadingMethod } from '../types/music';
import type { MusicalToken } from '../types/music';
import {
  CELL_REGISTRY,
  lookupCellById,
  type RhythmicCell,
  type RhythmicCellDefinition,
} from './rhythmicCellEngine';

export interface RhythmCellMcqOption {
  id: string;
  label: string;
  token: MusicalToken;
  takadimi: string;
}

/** IDs canónicos para distractores — nunca COMPOUND_BEAT ni nombres "X + Y". */
const MCQ_DISTRACTOR_POOL: string[] = [
  'NEGRA',
  'BLANCA',
  'PAR_CORCHEAS',
  'CORCHEA_SUELTA',
  'CUARTINA',
  'SILENCIO_NEGRA',
  'GALOPA',
  'TRESILLO_CORCHEAS',
];

function isMcqEligibleCell(cell: Pick<RhythmicCell, 'id' | 'name'> | undefined): cell is RhythmicCell {
  if (!cell) return false;
  if (cell.id === 'COMPOUND_BEAT') return false;
  if (String(cell.name).includes(' + ')) return false;
  return Boolean(lookupCellById(cell.id) || CELL_REGISTRY.some((entry) => entry.id === cell.id));
}

function cellToMcqOption(
  cell: RhythmicCell | RhythmicCellDefinition,
  method: RhythmReadingMethod,
): RhythmCellMcqOption {
  const token = cell.tokens[0] ?? 'Q';
  return {
    id: cell.id,
    label: formatCellMcqLabel(cell, method),
    token,
    takadimi: cell.takadimi,
  };
}

/** Etiqueta pedagógica para MCQ — vocabulario chileno + Takadimi entre paréntesis. */
export function formatCellMcqLabel(
  cell: Pick<RhythmicCell, 'id' | 'name' | 'takadimi'>,
  method: RhythmReadingMethod = 'tradicional',
): string {
  if (cell.id === 'COMPOUND_BEAT' || String(cell.name).includes(' + ')) {
    const def = lookupCellById(cell.id);
    if (def && def.id !== 'COMPOUND_BEAT') return cellToMcqOption(def, method).label;
    return 'Figura compuesta';
  }
  const def = lookupCellById(cell.id);
  const name = def?.name ?? cell.name;
  if (method === 'takadimi' && cell.takadimi && cell.takadimi !== 'Silencio') {
    return `${name} (${cell.takadimi})`;
  }
  if (method === 'kodaly' && cell.takadimi) {
    return `${name} (${cell.takadimi})`;
  }
  return name;
}

function durationDistance(a: number, b: number): number {
  return Math.abs(a - b);
}

function stableCorrectIndex(seed: string, slot: number, count: number): number {
  if (count <= 1) return 0;
  let hash = slot;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return hash % count;
}

export interface BuildRhythmCellMcqOptionsParams {
  correctCell: RhythmicCell;
  beatCells?: RhythmicCell[];
  maxOptions?: number;
  rhythmMethod?: RhythmReadingMethod;
  /** Semilla estable para posición de la respuesta correcta (p. ej. missingIndex). */
  shuffleSeed?: number;
}

/** Construye alternativas MCQ desde el registro — sin "Corchea + Negra". */
export function buildRhythmCellMcqOptions({
  correctCell,
  beatCells = [],
  maxOptions = 4,
  rhythmMethod = 'tradicional',
  shuffleSeed = 0,
}: BuildRhythmCellMcqOptionsParams): { options: RhythmCellMcqOption[]; correctIndex: number } {
  const pool = new Map<string, RhythmCellMcqOption>();

  for (const cell of beatCells) {
    if (!isMcqEligibleCell(cell)) continue;
    pool.set(cell.id, cellToMcqOption(cell, rhythmMethod));
  }

  for (const id of MCQ_DISTRACTOR_POOL) {
    const def = lookupCellById(id);
    if (!def || pool.has(def.id)) continue;
    pool.set(def.id, cellToMcqOption(def, rhythmMethod));
  }

  let resolvedCorrect = correctCell;
  if (!isMcqEligibleCell(correctCell)) {
    const tokens = correctCell.tokens.join('|');
    const fromRegistry = CELL_REGISTRY.find((entry) => entry.tokens.join('|') === tokens);
    if (fromRegistry) resolvedCorrect = { ...correctCell, ...fromRegistry };
  }

  if (isMcqEligibleCell(resolvedCorrect)) {
    pool.set(resolvedCorrect.id, cellToMcqOption(resolvedCorrect, rhythmMethod));
  }

  const correctId = resolvedCorrect.id;
  const correctDuration = resolvedCorrect.beatDuration;
  const correctOption = pool.get(correctId);
  if (!correctOption) {
    const fallback = cellToMcqOption(
      lookupCellById('NEGRA') ?? CELL_REGISTRY.find((e) => e.id === 'NEGRA')!,
      rhythmMethod,
    );
    return { options: [fallback], correctIndex: 0 };
  }

  const distractors = [...pool.values()]
    .filter((option) => option.id !== correctId)
    .sort((a, b) => {
      const defA = lookupCellById(a.id);
      const defB = lookupCellById(b.id);
      const distA = durationDistance(defA?.beatDuration ?? 1, correctDuration);
      const distB = durationDistance(defB?.beatDuration ?? 1, correctDuration);
      if (distA !== distB) return distA - distB;
      return a.label.localeCompare(b.label, 'es');
    })
    .slice(0, Math.max(1, maxOptions - 1));

  const merged = [correctOption, ...distractors];
  const targetIndex = stableCorrectIndex(correctId, shuffleSeed, merged.length);
  const options = [...merged];
  const currentIdx = options.findIndex((o) => o.id === correctId);
  if (currentIdx >= 0 && currentIdx !== targetIndex) {
    const [item] = options.splice(currentIdx, 1);
    options.splice(targetIndex, 0, item!);
  }

  return {
    options: options.slice(0, maxOptions),
    correctIndex: options.findIndex((o) => o.id === correctId),
  };
}

/** Resuelve la célula correcta para un tiempo del compás (complete / identify). */
export function correctCellForBeat(
  beatCells: RhythmicCell[],
  beatIndex: number,
): RhythmicCell | undefined {
  if (!beatCells.length) return undefined;
  const idx = Math.max(0, Math.min(beatIndex, beatCells.length - 1));
  const cell = beatCells[idx];
  if (!cell) return undefined;
  if (isMcqEligibleCell(cell)) return cell;
  const signature = cell.tokens.join('|');
  const fromRegistry = CELL_REGISTRY.find((entry) => entry.tokens.join('|') === signature);
  if (fromRegistry) {
    return {
      ...cell,
      id: fromRegistry.id,
      name: fromRegistry.name,
      takadimi: fromRegistry.takadimi,
    };
  }
  return cell;
}
